import { StaticLink } from '@silvertours/common-landingpages-view';
import { ResponsiveLogo } from '@silvertours/front-entities';
import { Theme } from '@silvertours/front-shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ComponentType,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  SVGProps,
} from 'react';
import { useMedia } from 'react-use';
import { HiddenLinksPopover, MobileMenu, Navigation } from './components';
import {
  Header as StyledHeader,
  IconButtonContainer,
  ActionArea,
  DesktopActions,
  ActionButtons,
  FloatingFrame,
} from './Header.styles';
import { useMobileMenus } from './hooks';

type MobileMenuProps = {
  id?: string;
  closeLabel: string;
  hide?: boolean;
  iconLabel: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  content: (closeMenu: () => void, open: boolean) => ReactNode;
  onClose?: () => void;
  /** The floating header also shows this menu on wide screens. */
  showInFloatingHeader?: boolean;
};

export type Props = {
  /**
   * `floating` shows a rounded bar on a green frame from the md breakpoint.
   * It has no navigation links and uses the desktop actions from md.
   */
  appearance?: 'default' | 'floating';
  desktopActions: Array<{
    component: ReactElement;
    hide?: boolean;
    key: string;
  }>;
  mobileMenus: MobileMenuProps[];
  /** import Header.Navigation for readymade component */
  desktopLinks: StaticLink[];
  translations: {
    navigationLabel: string;
    hiddenLinksLabel: string;
  };
} & HTMLAttributes<HTMLHeadElement>;

const Header = ({
  appearance = 'default',
  children,
  desktopActions,
  desktopLinks,
  mobileMenus,
  translations: { navigationLabel, hiddenLinksLabel },
  ...rest
}: Props) => {
  const { openMenu, setOpenMenu } = useMobileMenus();

  const actionAreaRef = useRef<HTMLDivElement>(null);
  const [menuTop, setMenuTop] = useState<number>(0);

  // The menus open below the header. The floating header is not sticky, so its
  // dropdown also follows the header out of the window when the page scrolls.
  // The other menus stay below the visible window edge.
  const followsHeaderOutOfView = appearance === 'floating';
  const positionMenu = useCallback(() => {
    if (actionAreaRef.current) {
      const { bottom } = actionAreaRef.current.getBoundingClientRect();
      const isVisible = bottom > 0;
      if (
        bottom !== menuTop &&
        (isVisible || (followsHeaderOutOfView && bottom < 0))
      ) {
        setMenuTop(bottom);
      }
    }
  }, [followsHeaderOutOfView, menuTop]);

  useEffect(() => {
    positionMenu();

    const header = actionAreaRef.current;
    const banner = document.querySelector('#infobanner');

    const ro = new ResizeObserver(positionMenu);
    if (header) ro.observe(header);
    if (banner) ro.observe(banner);

    window.addEventListener('scroll', positionMenu);

    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', positionMenu);
    };
  }, [actionAreaRef, positionMenu]);

  const [hiddenLinks, setHiddenLinks] = useState<ReactElement[]>([]);

  const getHiddenLinks = (links: ReactElement[]) => {
    setHiddenLinks(links);
  };

  const isFloating = appearance === 'floating';

  const wideViewPort = useMedia(
    `(min-width:${Theme.getBreakpoint(isFloating ? 'md' : 'lg')}px)`,
    false,
  );

  const renderMobileMenu = (
    {
      id,
      closeLabel,
      content,
      hide,
      icon,
      iconLabel,
      onClose,
      showInFloatingHeader,
    }: MobileMenuProps,
    index: number,
  ) => {
    const handleMenuClick = () => {
      setOpenMenu(index);
    };
    // The floating header shows its flagged menu as a dropdown card from md.
    const getAppearance = () => {
      if (!isFloating || !showInFloatingHeader) {
        return 'default';
      }
      return wideViewPort ? 'dropdown' : 'floating';
    };
    return hide ? null : (
      <MobileMenu
        id={id}
        appearance={getAppearance()}
        closeMenuLabel={closeLabel}
        renderContent={content}
        icon={icon}
        iconLabel={iconLabel}
        key={`actionButton-${id}-${iconLabel}`}
        toggleOpen={handleMenuClick}
        open={openMenu === index}
        onClose={onClose}
        topPosition={menuTop}
      />
    );
  };

  const desktopActionButtons = desktopActions.map(({ component, key, hide }) =>
    hide ? null : (
      <IconButtonContainer key={`actionButton-desktop-${key}`}>
        {component}
      </IconButtonContainer>
    ),
  );

  const renderWideActions = () =>
    isFloating ? (
      <ActionButtons>
        {desktopActionButtons}
        {mobileMenus.map((menu, index) =>
          menu.showInFloatingHeader ? renderMobileMenu(menu, index) : null,
        )}
      </ActionButtons>
    ) : (
      <DesktopActions>
        <Navigation
          label={navigationLabel}
          mode="desktop"
          links={desktopLinks}
          getHiddenElements={getHiddenLinks}
        />

        <ActionButtons>
          {desktopActionButtons}
          {Boolean(hiddenLinks.length) && (
            <HiddenLinksPopover label={hiddenLinksLabel}>
              {hiddenLinks}
            </HiddenLinksPopover>
          )}
        </ActionButtons>
      </DesktopActions>
    );

  const header = (
    <StyledHeader {...rest} ref={actionAreaRef} isFloating={isFloating}>
      <ResponsiveLogo />
      <ActionArea>
        {wideViewPort ? (
          renderWideActions()
        ) : (
          <ActionButtons id="header-action-buttons">
            {mobileMenus.map(renderMobileMenu)}
          </ActionButtons>
        )}
      </ActionArea>
    </StyledHeader>
  );

  return (
    <>
      {children}
      {isFloating ? <FloatingFrame>{header}</FloatingFrame> : header}
    </>
  );
};

export { Header };
